import {chain, sortBy} from 'lodash';
import {connect} from 'react-redux';

import {AsyncMoreStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ScrollDirectionsEnum} from '../../../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {MessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {CellsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {buildIndexMap} from '../../../../../srcCommon/helpers/itemIndexHelpers';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {makeMessageDateGetter, MessageDateGetter} from '../../../../helpers/models/messageHelpers';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {AppState} from '../../../../store/appState';
import {getComments, getConversations, getMessages} from '../../../../store/data/dataSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {
  FetchCellsCachePoliciesEnum,
  fetchMoreMessagesAsyncAction,
  navigateCellsAsyncAction,
} from '../../../../store/panels/cells/cellsPanelActions';
import {CellsPanelMessagesPageState} from '../../../../store/panels/cells/cellsPanelState';
import {CellsMessages, CellsMessagesContent} from './cellsMessages';

/*
 * Props.
 */

interface CellsMessagesContainerProps {
  panelId: CellsPanelId;
  page: CellsPanelMessagesPageState;
  cachePolicy: FetchCellsCachePoliciesEnum;
}

/*
 * Selectors.
 */

function getPage(_state: AppState, props: CellsMessagesContainerProps) {
  return props.page;
}

type GetPageMessages = AppParametricSelector<any, ReadonlyArray<MessageModel>>;
function makeGetPageMessages() {
  return createSelector([getPage, getMessages], (page, messages) =>
    chain(page.messageUids)
      .map((uid) => messages[uid])
      .compact()
      .value(),
  );
}

type GetDateGetter = AppParametricSelector<any, MessageDateGetter>;
function makeGetDateGetter() {
  return createSelector([getPage], (page) => makeMessageDateGetter(page.parameters));
}

/* eslint no-param-reassign: 0 */
type MessageDateMap = {[T: string]: number};
function makeGetSortDates(getPageMessages: GetPageMessages, getDateGetter: GetDateGetter) {
  return createSelector(
    [getPage, getPageMessages, getDateGetter, getConversations, getComments],
    (page, pageMessages, dateGetter, allConversations, allComments) => {
      // Find the sorter to use to get a message's date.
      const {sortDirection} = page.parameters;
      const isAscending = sortDirection === CellsSortDirectionsEnum.ASCENDING;

      return pageMessages.reduce<MessageDateMap>((result, message) => {
        const messageDate = dateGetter(message);
        result[message.uid] = isAscending ? messageDate : -messageDate;
        return result;
      }, {});
    },
  );
}

function makeGetCellsContent(getDateGetter: GetDateGetter) {
  const getPageMessages = wrapInShallowSelector(makeGetPageMessages());
  const getSortDates = wrapInShallowSelector(makeGetSortDates(getPageMessages, getDateGetter));
  const getSortedMessages = createSelector([getPageMessages, getSortDates], (pageMessages, sortDates) =>
    sortBy(pageMessages, (m) => sortDates[m.uid]),
  );
  const getSortedMessageUids = wrapInShallowSelector(
    createSelector([getSortedMessages], (sortedMessages) => sortedMessages.map((m) => m.uid)),
  );
  const getMessageIndexes = createSelector([getSortedMessageUids], (sortedMessageUids) =>
    buildIndexMap(sortedMessageUids),
  );

  return createSelector(
    [getSortedMessages, getSortedMessageUids, getMessageIndexes],
    (sortedMessages, sortedMessageUids, messageIndexes) => {
      const result: CellsMessagesContent = {
        messages: sortedMessages,
        messageUids: sortedMessageUids,
        messageIndexes,
      };
      return result;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CellsMessagesContainerProps>()(() => {
  const getDateGetter = makeGetDateGetter();

  return {
    user: getUser,
    content: makeGetCellsContent(getDateGetter),
    dateGetter: getDateGetter,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<CellsMessagesContainerProps>()(
  ({panelId, page, cachePolicy}) => ({
    onRetryClick: () => navigateCellsAsyncAction(panelId, page.parameters),
    onRequestMoreAsync: (direction) => {
      const {moreStatusTop, moreStatusBottom, topBoundary, bottomBoundary} = page;

      // Check if we're allowed to load more in that direction.
      const isTop = direction === ScrollDirectionsEnum.TOP;
      const moreStatus = isTop ? moreStatusTop : moreStatusBottom;
      if (moreStatus !== AsyncMoreStatusesEnum.HAS_MORE) {
        throw new Error('Nothing to load.');
      }

      // Find the boundary that we'll use as reference for the next batch.
      const referenceBoundary = isTop ? topBoundary : bottomBoundary;
      if (!referenceBoundary) {
        throw new Error('A reference boundary could not be found.');
      }

      return fetchMoreMessagesAsyncAction(
        panelId,
        page.uid,
        page.parameters,
        direction,
        referenceBoundary,
        cachePolicy,
      );
    },
  }),
);

export const CellsMessagesContainer = connect(mapStateToProps, mapDispatchToProps)(CellsMessages);
